import { c as _c } from "react/compiler-runtime";
/* eslint-disable tailwindcss/no-contradicting-classname */
import React, { ReactNode, useMemo, useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Skeleton, Switch, Tooltip, Menu, Button, Checkbox, Combobox, Divider, Badge, Indicator, useCombobox, Loader, Alert } from '@mantine/core';
import { usePortal } from 'components/portal/portal';
import { Portal } from 'components/portal/Portal';
import { StyleProps } from 'components';
import { questionStatusMd } from 'shared/forecast/questionStatus';
import { QuestionSubquestions, QuestionTileItems } from './QuestionTile';
import { QuestionUserFilter, zQuestionUserFilter, useQuestionsPageQuery, QuestionPageRow } from './useQuestions';
import { z } from 'zod';
import { useQueryString } from 'utils/useQueryString';
import { ChildrenProps } from 'shared/ui/uiType';
import { zx } from 'shared/type';
import { dueDate, zQuestionStatus } from 'shared/forecast/question';
import { HrText, LabelEmptyList, Masonry, WrapIf } from 'shared/ui';
import Link from 'next/link';
import { QuestionStatusFilter, questionStatusItem } from './QuestionStatus';
import { groupBy, isEmpty, isString, mapValues, partition, pick, sortBy } from 'remeda';
import { CommentsStats } from 'components/discussion/Comments';
import { P, match } from 'ts-pattern';
import { DueLabel } from './Due';
import { attachmentItems, QuestionAttachmentList } from './QuestionAttachment';
import { nav } from 'components/portal/nav';
import { doSafe, okOrThrow } from 'shared/promise';
import { NewQuestionPopover } from './QuestionNew';
import { QuestionImport } from './QuestionImport';
import { HiArrowLeft, HiChevronDown, HiDuplicate, HiOutlineDocumentText, HiOutlineDuplicate, HiRefresh, HiTrash } from 'react-icons/hi';
import { openConfirmModal } from '@mantine/modals';
import { OverflowMenu } from 'components/Menu';
import Image from 'next/image';
import NoQuestions from '../../../public/no-questions.png';
import { TbGripVertical } from 'react-icons/tb';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Workspace } from 'components/portal/workspace';
import { ActionCard } from 'components/ActionPanel';
import { twMerge } from 'tailwind-merge';
import { moveById } from 'components/form/OrderableList';
import { Publish } from 'api/publish';
import { delay } from 'shared/promise';
import { MdOutlineLibraryAddCheck } from 'react-icons/md';
import { QuestionExport } from './QuestionExport';
import { useMutation } from '@tanstack/react-query';
import { showError } from 'components/notify';
import { AppTitle } from 'components/portal/Title';
import { useMyForecastProgress } from './progress';
import { isQuestionTasked, useParticipation } from './participation';
import { sortQuestions } from 'shared/forecast/questionOrder';
import { dupeQuestion } from './dupe';
const zQuestionsQuery = z.object({
  workspace: z.string().optional(),
  status: z.array(zQuestionStatus).default([]),
  showDraft: zx.nullish.bool.default(false),
  [`selected-questions`]: z.array(z.string()).default([]),
  ['highlight-questions']: z.array(z.string()).default([])
});
export const Questions = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <QuestionsInner />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Portal>{t1}{children}</Portal>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const QuestionsInner = () => {
  const {
    profile,
    workspace_id,
    refetchProfilesUI
  } = usePortal();
  const router = useRouter();
  const {
    query,
    setQuery
  } = useQueryString(zQuestionsQuery);
  const userFilter = zQuestionUserFilter.parse(query);
  const statusFilter = query?.status ?? [];
  const selectedQuestions = query?.['selected-questions'] ?? [];
  const highlightQuestions = query?.['highlight-questions'] ?? [];
  const isAdmin = profile.hasRole('admin');
  const isGlobal = workspace_id == 'global';
  const [dragMode, setDragMode] = useState(false);
  const workspaceForm = Workspace.useAdminWorkspace();
  // const questionOrder = workspaceForm.form.values?.questionOrder ?? {active: [], inactive: []}

  const importSampleMutation = useMutation({
    mutationFn: () => Publish.importQuestionFromUrl({
      workspace_id,
      questionIds: [],
      importUrl: `${window.location.origin}/examples.swift.zip`,
      includeForecasts: true,
      includeComments: true
    }).then(okOrThrow),
    onSuccess: result => {
      const question = result.importedQuestions.at(0);
      if (!question) return;
      router.push(nav.question.getUrl({
        workspace: workspace_id,
        question_id: question.id
      }).url);
    },
    onError: err => {
      showError(err, 'Error importing example question');
    }
  });
  const {
    questions: questionsRaw,
    isError,
    error,
    isLoading,
    refetch,
    update,
    updateAll
  } = useQuestionsPageQuery({
    userFilter
  });
  const questions = useMemo(() => {
    if (!questionsRaw || !workspaceForm.form.values) return undefined;
    return sortQuestions(questionsRaw.filter(q => isEmpty(statusFilter) || statusFilter.includes(q.cfg.status)), workspaceForm.form.values.questionOrder);
  }, [query?.status, questionsRaw, workspaceForm.form.values?.questionOrder]);
  useEffect(() => {
    if (highlightQuestions.length > 0) {
      const timer = setTimeout(() => {
        setQuery({
          ['highlight-questions']: []
        });
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [highlightQuestions, setQuery]);
  const selectCombo = useCombobox({
    onDropdownClose: () => selectCombo.resetSelectedOption()
  });
  return match({
    profile,
    isError,
    isLoading,
    isGlobal
  }).with({
    isGlobal: true
  }, () => <div className='p-4 text-center text-gray-600'>
        You are in the global workspace which is used to administer global access and configuration (not manage
        questions)
      </div>).with({
    profile: P.when(p => !p.hasPermission('question-view'))
  }, ({
    profile: profile_0
  }) => profile_0.hasPermission('result-view') ? <ReaderQuestions /> : <></>).with({
    isError: true
  }, () => <div className='flex justify-center pt-2'>
        <Alert color='red' title='Error loading page'>
          {error?.toString()}
        </Alert>
      </div>).with({
    isLoading: true
  }, () => <div className='space-y-6 w-full p-4'>
        <QuestionSkeleton />
        <QuestionSkeleton />
      </div>).otherwise(() => {
    const noQuestions = questions?.length === 0;
    const questionImportProps = {
      onImportComplete: ({
        importedQuestions
      }: Publish.ImportQuestionResult) => {
        refetch();
        refetchProfilesUI();
        setQuery({
          ['highlight-questions']: importedQuestions.map(q_0 => q_0.id)
        });
      }
    };
    return <div className='w-full pb-[10em] flex flex-col items-center' data-test-id='question-list'>
          <AppTitle title={`${profile.workspace.name} - Questions`} />
          {!dragMode && <ActionCard classNames={{
        content: 'flex flex-wrap gap-x-4 items-center justify-center mx-2 py-1',
        root: ''
      }}>
              <div className='flex items-center gap-2 flex-wrap'>
                {isAdmin && !noQuestions && <NewQuestionPopover />}
                {!noQuestions && <>
                    <QuestionStatusFilter value={statusFilter} onChange={status => setQuery({
              status
            })} showClear counts={questionsRaw ? mapValues(groupBy(questionsRaw, q_1 => q_1.cfg.status), qs => qs.length) : {}} hideZeros />
                  </>}
                {isAdmin && !noQuestions && <>
                    <QuestionFilterItems {...{
              filter: userFilter,
              setFilter: setQuery
            }} />
                    <QuestionImport {...questionImportProps} />
                  </>}
              </div>
              {isAdmin && !noQuestions && <div className='flex items-center gap-2'>
                  <Combobox store={selectCombo} classNames={{}} onOptionSubmit={(value: string) => {
            if (value == 'select-all') setQuery({
              ['selected-questions']: questions?.map(q_2 => q_2.id) ?? []
            });else if (value == 'clear-all') setQuery({
              ['selected-questions']: []
            });else if (value && value.startsWith('question-')) {
              const questionId = value.replace('question-', '');
              const isSelected = selectedQuestions.includes(questionId);
              const newSelected = isSelected ? selectedQuestions.filter(id => id != questionId) : [...selectedQuestions, questionId];
              setQuery({
                ['selected-questions']: newSelected
              });
            }
          }}>
                    <Combobox.Target>
                      <div className={twMerge('flex items-center gap-1 cursor-pointer ', selectedQuestions.length == 0 && 'opacity-60')} onClick={() => selectCombo.toggleDropdown()}>
                        <Badge color={selectedQuestions.length ? 'primary' : 'gray'}>{selectedQuestions.length}</Badge>
                        <div className='font-semibold text-sm'>selected</div>
                        <HiChevronDown />
                      </div>
                    </Combobox.Target>
                    <Combobox.Dropdown className=' max-h-[80vh] overflow-y-auto'>
                      <Combobox.Options className='max-w-[30em]'>
                        <Combobox.Option value='select-all' className='flex items-center gap-2 !p-3' disabled={questions?.length == 0}>
                          <MdOutlineLibraryAddCheck /> Select All
                        </Combobox.Option>
                        <Combobox.Option value='clear-all' className='flex items-center gap-2 !p-3' disabled={selectedQuestions.length == 0}>
                          <HiOutlineDuplicate /> Clear selection
                        </Combobox.Option>
                        {selectedQuestions.length > 0 && <Divider className='my-2' />}
                        {questions?.filter(q_3 => selectedQuestions.includes(q_3.id)).map(q_4 => <Combobox.Option className='flex items-center gap-2' key={q_4.id} value={`question-${q_4.id}`}>
                              <Checkbox size='xs' checked />
                              {q_4.cfg.title}
                            </Combobox.Option>)}
                      </Combobox.Options>
                    </Combobox.Dropdown>
                  </Combobox>
                  {selectedQuestions.length > 0 && <>
                      <SelectionButtonWrap selectedQuestions={selectedQuestions} description='export questions'>
                        <QuestionExport questionIds={selectedQuestions} workspace_id={workspace_id} />
                      </SelectionButtonWrap>
                      <SelectionButtonWrap selectedQuestions={selectedQuestions} description='delete questions'>
                        <Button variant='subtle' leftSection={<HiTrash color='var(--mantine-color-red-6)' />} color='red' disabled={selectedQuestions.length == 0} onClick={() => {
                if (selectedQuestions.length == 0) return;
                openConfirmModal({
                  title: 'Delete selected questions?',
                  centered: true,
                  children: <span>
                                  Are you sure you want to delete {selectedQuestions.length} selected question
                                  {selectedQuestions.length == 1 ? '' : 's'}?
                                </span>,
                  labels: {
                    confirm: 'Delete',
                    cancel: 'Cancel'
                  },
                  confirmProps: {
                    color: 'red',
                    variant: 'filled'
                  },
                  onConfirm: async () => {
                    await updateAll({
                      ids: selectedQuestions,
                      update: {
                        deleted: true
                      }
                    });
                    setQuery({
                      ['selected-questions']: []
                    });
                  }
                });
              }}>
                          Delete
                        </Button>
                      </SelectionButtonWrap>
                    </>}
                </div>}
            </ActionCard>}
          {dragMode && isAdmin && <ActionCard className='justify-center gap-3'>
              <Button variant='filled' leftSection={<HiArrowLeft />} onClick={() => setDragMode(false)} className=''>
                Back to Questions
              </Button>
              <Button variant='outline' leftSection={<HiRefresh />} onClick={() => workspaceForm.form.setFieldValue('questionOrder', {
          active: [],
          inactive: []
        })}>
                Reset Order
              </Button>
            </ActionCard>}
          {match(questions).with(P.nullish, () => <></>).with([], () => <div className='flex flex-col items-center gap-10 pt-10'>
                {isAdmin && <h3>Welcome to your fresh workspace </h3>}
                <div className='relative'>
                  <Image src={NoQuestions} alt='No questions' className='opacity-50' />
                  <div className='absolute top-[40%] right-[30%] translate-y-1/2 translate-x-1/2'>
                    {isAdmin ? <NewQuestionPopover buttonProps={{
              variant: 'filled',
              size: 'lg',
              className: 'shadow-lg',
              children: 'Start a new question'
            }} /> : <div className='text-gray-600 text-xl font-extrabold text-stroke-white'>No questions yet</div>}
                  </div>
                  {isAdmin && <div className='absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-4 items-center'>
                      <Button variant='filled' color='teal' leftSection={importSampleMutation.isPending ? <Loader size='sm' /> : <HiOutlineDocumentText />} onClick={() => importSampleMutation.mutate()}>
                        Open example question
                      </Button>
                      <QuestionImport {...questionImportProps}>Import questions</QuestionImport>
                    </div>}
                </div>
                {isAdmin && <div className='text-lg'>
                    You might also want to{' '}
                    <Link href={nav.admin.getUrl({
            workspace: workspace_id,
            tab: 'users'
          }).mergeQuery({
            adminSections: 'emailInvites'
          }).url}>
                      invite your team members
                    </Link>
                  </div>}
              </div>).otherwise(qs_0 => {
        const [questionsActiveRaw, questionsInactive] = partition(qs_0, q_5 => questionStatusMd[q_5.cfg.status].active == true);
        const [questionsTasked, questionsActive] = partition(questionsActiveRaw, q_6 => isQuestionTasked(profile, q_6.cfg));
        const tables = [{
          questions: questionsTasked,
          label: 'Participating & Active',
          orderList: 'active' as const
        }, {
          questions: questionsActive,
          label: 'Active',
          orderList: 'active' as const
        }, {
          questions: questionsInactive,
          label: 'Inactive',
          orderList: 'inactive' as const
        }].filter(t => t.questions.length > 0);
        return <div className='space-y-6 w-full'>
                  {tables.map(t_0 => <QuestionTable key={t_0.label} questions={t_0.questions} selectedQuestions={selectedQuestions} onToggleSelection={questionId_0 => {
            const isSelected_0 = selectedQuestions.includes(questionId_0);
            const newSelected_0 = isSelected_0 ? selectedQuestions.filter(id_0 => id_0 != questionId_0) : [...selectedQuestions, questionId_0];
            setQuery({
              ['selected-questions']: newSelected_0
            });
          }} deleteQuestion={async id_1 => {
            await update({
              id: id_1,
              deleted: true
            });
          }} label={tables.length > 1 && t_0.label} dragMode={dragMode} setIdOrder={({
            fromId,
            toId
          }) => workspaceForm.form.setFieldValueDynamic(`questionOrder.${t_0.orderList}`, moveById(t_0.questions.map(q_7 => q_7.id), fromId, toId))} setDragMode={setDragMode} debugName={t_0.label == 'Active' ? 'active' : undefined} refetch={refetch} />)}
                </div>;
      })}
        </div>;
  });
};
const SelectionButtonWrap = t0 => {
  const $ = _c(11);
  const {
    children,
    selectedQuestions,
    description
  } = t0;
  const t1 = selectedQuestions.length == 0;
  let t2;
  if ($[0] !== description) {
    t2 = c => <Tooltip label={`Make a selection to ${description}`}>{c}</Tooltip>;
    $[0] = description;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      indicator: "!text-[8px] font-bold"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = selectedQuestions.length == 0;
  let t5;
  if ($[3] !== children || $[4] !== selectedQuestions.length || $[5] !== t4) {
    t5 = <Indicator label={selectedQuestions.length} size={13} classNames={t3} position="bottom-end" offset={6} disabled={t4}>{children}</Indicator>;
    $[3] = children;
    $[4] = selectedQuestions.length;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t5) {
    t6 = <WrapIf if={t1} wrap={t2}>{t5}</WrapIf>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
const QuestionTable = t0 => {
  const $ = _c(15);
  const {
    questions,
    selectedQuestions,
    onToggleSelection,
    label,
    deleteQuestion,
    dragMode,
    setDragMode,
    setIdOrder,
    refetch
  } = t0;
  const {
    profile
  } = usePortal();
  let t1;
  if ($[0] !== profile) {
    t1 = profile?.hasRole("admin");
    $[0] = profile;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isAdmin = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      coordinateGetter: sortableKeyboardCoordinates
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, t2));
  let t3;
  if ($[3] !== deleteQuestion || $[4] !== dragMode || $[5] !== isAdmin || $[6] !== label || $[7] !== onToggleSelection || $[8] !== questions || $[9] !== refetch || $[10] !== selectedQuestions || $[11] !== sensors || $[12] !== setDragMode || $[13] !== setIdOrder) {
    t3 = questions.length == 0 ? <></> : <div>{label ? <HrText className="my-2" classNames={{
        label: "mx-6 px-4 font-bold text-sm uppercase opacity-70"
      }}>{label}</HrText> : <div className="h-2" />}<DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={() => setDragMode(true)} onDragEnd={async event => {
        const {
          active,
          over
        } = event;
        if (!over || !questions || !isString(active.id) || !isString(over.id)) {
          return;
        }
        const question = questions?.find(q => q.id === active.id);
        if (!question) {
          return;
        }
        if (active.id == over.id) {
          return;
        }
        await setIdOrder({
          fromId: active.id,
          toId: over.id
        });
      }}><SortableContext items={questions.map(_temp15)} strategy={verticalListSortingStrategy}>{dragMode && isAdmin ? <div className="flex justify-center w-full"><div className="space-y-2 w-full max-w-[50em]">{questions.map(_temp16)}</div></div> : <Masonry className="w-full md:px-2 gap-6" colWidthBasis={500} classNames={{
            column: "gap-6"
          }}>{questions.map(q_2 => <QuestionItemContent key={q_2.id} question={q_2} isSelected={selectedQuestions.includes(q_2.id)} onToggleSelection={onToggleSelection} deleteQuestion={deleteQuestion} showDragHandle={isAdmin} sortable={isAdmin} refetch={refetch} />)}</Masonry>}</SortableContext></DndContext></div>;
    $[3] = deleteQuestion;
    $[4] = dragMode;
    $[5] = isAdmin;
    $[6] = label;
    $[7] = onToggleSelection;
    $[8] = questions;
    $[9] = refetch;
    $[10] = selectedQuestions;
    $[11] = sensors;
    $[12] = setDragMode;
    $[13] = setIdOrder;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const content = t3;
  return content;
};
type QuestionFilterPopState = Omit<QuestionUserFilter, 'question_id'>;
export type QuestionFilterProps = {
  filter?: QuestionFilterPopState;
  setFilter?: (q: QuestionFilterPopState) => void;
};
export const QuestionFilterItems = t0 => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      showDraft: false
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const localState = useState(t1);
  const filter = props.filter ?? localState[0];
  const setFilter = props.setFilter ?? localState[1];
  const {
    workspace: t2
  } = usePortal();
  const {
    advancedUI
  } = t2;
  let t3;
  if ($[4] !== advancedUI || $[5] !== className || $[6] !== filter || $[7] !== setFilter) {
    t3 = advancedUI && <Tooltip label="If enabled, will show questions that are in draft from other users"><div className="pl-2"><Switch className={className} label="show all drafts" checked={filter.showDraft} onChange={d => setFilter({
          ...filter,
          showDraft: d.currentTarget.checked
        })} /></div></Tooltip>;
    $[4] = advancedUI;
    $[5] = className;
    $[6] = filter;
    $[7] = setFilter;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const ReaderQuestions = () => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      userFilter: {
        showDraft: false
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    questions
  } = useQuestionsPageQuery(t0);
  let t1;
  if ($[1] !== questions) {
    t1 = match(questions).with(P.nullish, _temp17).with([], _temp18).otherwise(_temp21);
    $[1] = questions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="px-2 w-full">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const ReaderQuestion = t0 => {
  const $ = _c(20);
  const {
    question: t1,
    className
  } = t0;
  const {
    id,
    created_at,
    cfg
  } = t1;
  const {
    title,
    status,
    subTitle
  } = cfg;
  const {
    profile: myProfile
  } = usePortal();
  let t2;
  if ($[0] !== status) {
    t2 = questionStatusItem(questionStatusMd, status);
    $[0] = status;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const statusItem = t2;
  if (!cfg || !myProfile) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <></>;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  const t3 = `/results/${id}`;
  let t4;
  if ($[3] !== title) {
    t4 = <h3 className="">{title}</h3>;
    $[3] = title;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== subTitle) {
    t5 = <div className="text-gray-600">{subTitle}</div>;
    $[5] = subTitle;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className="grow">{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <Link className="mb-1 flex w-full items-center justify-between rounded-md px-2 py-1 hover:bg-gray-100" href={t3}>{t6}</Link>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== created_at || $[14] !== statusItem) {
    t8 = <div className="flex items-center gap-2 pl-2"><QuestionTileItems className="ml-2" {...{
        statusItem,
        created_at
      }} /></div>;
    $[13] = created_at;
    $[14] = statusItem;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== className || $[17] !== t7 || $[18] !== t8) {
    t9 = <div className={className}>{t7}{t8}</div>;
    $[16] = className;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
const SortableQuestionItem = ({
  question
}: {
  question: QuestionPageRow;
}) => {
  const {
    profilesUI
  } = usePortal();
  const s = useSortable({
    id: question.id
  });
  return <div ref={s.setNodeRef} {...s.attributes} {...s.listeners} style={{
    transform: CSS.Translate.toString(s.transform || null),
    transition: s.transition,
    zIndex: s.isDragging ? 100 : 0
  }} className='relative '>
      <div className={twMerge(`flex w-full items-center rounded-md p-3 cursor-grab`, s.isDragging ? 'glass' : 'border')}>
        <div className='p-1 active:cursor-grabbing flex-shrink-0 pr-4'>
          <TbGripVertical size={18} className='stroke-gray-400' />
        </div>
        <div className='flex-grow min-w-0 flex flex-col justify-center'>
          <h3 className=' font-medium leading-tight'>{question.cfg.title}</h3>
        </div>
        <div className='flex items-center gap-2 flex-shrink-0 ml-2'>
          <div className='w-[10em]'>
            <QuestionTileItems statusItem={{
            md: questionStatusMd[question.cfg.status],
            value: question.cfg.status,
            disabled: undefined
          }} created_at={question.created_at} />
          </div>
          <QuestionTileItems profile={profilesUI?.[question.cfg.owner ?? question.created_by]} />
        </div>
      </div>
    </div>;
};
const QuestionItemContent = ({
  question: q,
  isSelected,
  onToggleSelection,
  deleteQuestion,
  showDragHandle = false,
  sortable = false,
  refetch
}: {
  question: QuestionPageRow;
  isSelected: boolean;
  onToggleSelection: (id: string) => void;
  deleteQuestion: (id: string) => Promise<void>;
  showDragHandle?: boolean;
  sortable?: boolean;
  refetch: () => void;
}) => {
  const {
    profilesUI,
    profile,
    readonly
  } = usePortal();
  const isAdmin = profile?.hasRole('admin');
  const participation = useParticipation({
    question_id: q.id,
    cfg: q.cfg
  });
  const sort = useSortable({
    id: q.id,
    disabled: !sortable || !isAdmin
  });
  const ref = useRef<HTMLDivElement>(null);
  const progress = useMyForecastProgress({
    question_id: q.id
  });
  const {
    query,
    setQuery
  } = useQueryString(zQuestionsQuery.pick({
    ['highlight-questions']: true
  }));
  const highlight = query?.['highlight-questions']?.includes(q.id);
  useEffect(() => {
    if (!highlight || !ref.current) return;
    delay(1).then(() => ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    }));
  }, [highlight, ref.current]);
  const setRefs = (node: HTMLDivElement | null) => {
    sort.setNodeRef(node);
    ref.current = node;
  };
  const dupeMutate = useMutation({
    mutationFn: async ({
      copy_forecasts
    }: {
      copy_forecasts: boolean;
    }) => dupeQuestion({
      type: 'question',
      copy_forecasts,
      profile,
      id: q.id
    }).then(r => {
      if (!r.ok) throw new Error(r.message);
      return r.value;
    }),
    onSuccess: newId => {
      refetch();
      setQuery({
        ['highlight-questions']: [newId]
      });
    }
  });
  return <div ref={setRefs} className={twMerge(`max-w-[60em] border rounded-md p-2 group w-full relative`, sortable && isAdmin && (sort.isDragging ? 'shadow-md' : ''), highlight ? 'bg-primary/10' : '')}>
      {showDragHandle && isAdmin && <div {...sort.attributes} {...sort.listeners} className='absolute top-1/2 left-0 opacity-0 group-hover:opacity-100 transition-opacity z-10 transform -translate-y-1/2 -translate-x-1/2 bg-[var(--mantine-color-body)]'>
          <div className='cursor-grab active:cursor-grabbing rounded-sm overflow-clip w-4 flex items-center justify-center'>
            <TbGripVertical size={20} className='stroke-gray-400' />
          </div>
        </div>}
      <div className='flex items-start'>
        <Link data-test-id={`question-link-${q.id}`} className='mb-1 flex w-full items-center justify-between rounded-md px-2 py-1 hover:bg-gray-100' href={nav.question.getUrl({
        workspace: q.workspace_id,
        question_id: q.id
      }).url}>
          <div className='grow'>
            <h3 className=' text-balance'>{q.cfg.title}</h3>
            <div className='flex items-center gap-2'>
              <div className='text-gray-600'>{q.cfg.subTitle}</div>
            </div>
          </div>
        </Link>
        <div className='flex items-center gap-2 ml-auto'>
          {!readonly && isAdmin && <OverflowMenu className='group-hover:opacity-100 opacity-0' width='max-content'>
              <Menu.Item leftSection={<HiTrash className='text-red-600' />} onClick={() => ((q_0: QuestionPageRow) => {
            openConfirmModal({
              title: 'Delete question?',
              centered: true,
              children: <span>
                          Are you sure you want to delete <b>{q_0.cfg.title}</b>?
                        </span>,
              labels: {
                confirm: 'Delete',
                cancel: 'Cancel'
              },
              confirmProps: {
                color: 'red',
                variant: 'filled'
              },
              onConfirm: () => {
                deleteQuestion(q_0.id);
              }
            });
          })(q)}>
                Delete
              </Menu.Item>
              <Menu.Item leftSection={<HiDuplicate />} onClick={() => dupeMutate.mutate({
            copy_forecasts: false
          })}>
                Duplicate
              </Menu.Item>
              <Menu.Item leftSection={<HiDuplicate />} onClick={() => dupeMutate.mutate({
            copy_forecasts: true
          })}>
                Duplicate with forecasts
              </Menu.Item>
            </OverflowMenu>}
          {isAdmin && <div className={`transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
              <Checkbox checked={isSelected} onChange={() => onToggleSelection(q.id)} size='sm' />
            </div>}
        </div>
      </div>
      <div className='flex items-center gap-4 flex-wrap'>
        <QuestionTileItems statusItem={{
        md: questionStatusMd[q.cfg.status],
        value: q.cfg.status,
        disabled: undefined
      }} participation={participation} progress={progress} {...pick(q, ['forecasters_total', 'forecasts_from', 'forecasts_to', 'created_at'])} />
        {q.total_comments ? <CommentsStats total={q.total_comments} unread={q.unread_comments} /> : undefined}
        <QuestionTileItems profile={profilesUI?.[q.cfg.owner ?? q.created_by]}
      // participation={participation}
      publish={{
        id: q.id,
        publish: q.cfg.publish,
        liveView: q.cfg.liveView
      }} />
        {doSafe(dueDate(q.cfg), d => d && <DueLabel date={d} />)}
      </div>
      <QuestionAttachmentList attachments={attachmentItems(q.cfg.attachments)} className='flex-row flex-wrap' />
      <QuestionSubquestions question_id={q.id} subquestions={q.subquestions} />
    </div>;
};
const QuestionSkeleton = () => {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton width="15em" height="1.8em" radius="md" />;
    t1 = <Skeleton width="20em" height="1.1em" radius="md" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="space-y-2">{t0}{t1}<div className="flex items-center gap-4"><div><Skeleton height={20} circle={true} /></div><Skeleton height="1.2em" width="8em" radius="xl" /><div><Skeleton height="1.5em" width="1.5em" radius="md" /></div></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
function _temp15(q_0) {
  return q_0.id;
}
function _temp16(q_1) {
  return <SortableQuestionItem key={q_1.id} question={q_1} />;
}
function _temp17() {
  return <QuestionSkeleton />;
}
function _temp18() {
  return <LabelEmptyList className="mt-20">No questions yet</LabelEmptyList>;
}
function _temp19(q) {
  return q.created_at;
}
function _temp20(q_0) {
  return <ReaderQuestion key={q_0.id} question={q_0} />;
}
function _temp21(qs) {
  return <Masonry maxColWidth={600}>{sortBy(qs, [_temp19, "desc"]).map(_temp20)}{" "}</Masonry>;
}